/**
 * @fileoverview Provides file name context by searching for files matching words in a prompt within a given directory. It uses `getFilesMatchingNames` and `fileNameContextProvider` to return a `FolderContext` object containing system information and file contents. Key functions include `getFilesMatchingNames` and `fileNameContextProvider`.
 */

import { statSync } from 'fs'
import { FolderContext } from './context_types'
import { getFileData, walkFolder } from './folder'
import { debug_block } from '@iterator/shared-node/util/logging'
import { basename } from 'path'
import { SparsePath } from '@iterator/shared-node/util/sparse_path'

export function fileNameContextProvider(
  directory: SparsePath,
  prompt?: string,
): Promise<FolderContext> {
  if (!prompt) {
    throw new Error('Prompt is required for file name context provider')
  }
  const { matching } = getFilesMatchingNames(prompt, directory)
  const paths = matching.length ? matching : []
  debug_block('File name context provider paths', paths)
  return Promise.resolve({
    system: `Project root: ${directory}\n${paths.join('\n')}`,
    texts: paths.map((file) => getFileData(file, true).contents),
  })
}

export function getFilesMatchingNames(
  prompt: string,
  directory: SparsePath,
): { allPaths: string[]; matching: string[] } {
  // Extract words from prompt, filtering out common words and punctuation
  const words = prompt
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 2)
    .filter((word) => !['the', 'and', 'for', 'that', 'with'].includes(word))

  const allPaths: string[] = []
  const matchingFiles: string[] = []
  debug_block('words to match file names', words)
  walkFolder(directory, (path) => {
    allPaths.push(path)
    if (statSync(path).isDirectory()) {
      return
    }
    const fileName = basename(path).toLowerCase()
    if (words.some((word) => fileName === word)) {
      matchingFiles.push(path)
    }
  })

  return { matching: matchingFiles, allPaths }
}
